import { createElement } from "react";
import { AgGrid, Flex, Text, HelpIndicator, Tag } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { providerStatusCell, entitiesCell, cellWithEllipsis } from "./styles_vanilla.css";
import { BankAccountRowItem, InvestmentEntity } from "./types";
export enum SupportedColumns {
  BankAccountId = "bankAccountId",
  AccountOwner = "bankAccountName",
  Bank = "bankName",
  AccountNumber = "bankInfoRedacted",
  InvestmentEntities = "investmentEntities",
  Fides = "fidesEnabled",
  RPA = "rpaEnabled",
  FundRecs = "fundRecsEnabled",
}

export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.BankAccountId, "Bank account ID"],
  [SupportedColumns.AccountOwner, "Account owner"],
  [SupportedColumns.Bank, "Bank"],
  [SupportedColumns.AccountNumber, "Account number"],
  [SupportedColumns.InvestmentEntities, "Investment entity"],
  [SupportedColumns.Fides, "Fides"],
  [SupportedColumns.RPA, "RPA"],
  [SupportedColumns.FundRecs, "FundRecs"],
]);

const ProviderStatusCellRenderer = ({ value }: { value: boolean }) => {
  if (value) {
    return createElement(
      "div",
      { className: providerStatusCell },
      createElement(
        Flex,
        { alignItems: "center", gap: 8 },
        createElement(Icon, {
          type: "check-circle",
          color: Colors.Success600,
          size: "small",
        }),
        createElement(Text, { color: Colors.Success600 }, "Enabled"),
      ),
    );
  }
  return createElement(
    "div",
    { className: providerStatusCell },
    createElement(Text, { color: Colors.Neutral600 }, "--"),
  );
};

const InvestmentEntitiesCellRenderer = ({ value }: { value: InvestmentEntity[] }) => {
  if (!value || value.length === 0) {
    return createElement(
      "div",
      { className: entitiesCell },
      createElement(Text, { color: Colors.Neutral600 }, "—"),
    );
  }

  // Single entity - just show the name
  if (value.length === 1) {
    return createElement(
      "div",
      { className: entitiesCell },
      createElement(Text, null, value[0].entityName),
    );
  }

  // Multiple entities - show first name with a badge showing count and tooltip with all entities
  const tooltipContent = createElement(
    "div",
    { style: { display: "flex", flexDirection: "column", gap: Sizes.XS } },
    value.map((entity) => createElement("div", { key: entity.entityId }, entity.entityName)),
  );

  const badgeElement = createElement(Tag, { variant: "neutral" }, `+${value.length - 1}`);

  return createElement(
    "div",
    { className: entitiesCell },
    createElement(Text, null, value[0].entityName),
    // eslint-disable-next-line react/no-children-prop -- HelpIndicator requires children in props type
    createElement(HelpIndicator, {
      id: `entities-tooltip-${value[0].entityId}`,
      placement: "top",
      content: tooltipContent,
      children: badgeElement,
    }),
  );
};

// Common cell style for vertical centering
const verticalCenterStyle = { display: "flex", alignItems: "center", overflow: "hidden" };
const centerStyle = { display: "flex", alignItems: "center", justifyContent: "center" };

export const columnDefinitions: AgGrid.ColDef<BankAccountRowItem>[] = [
  {
    field: SupportedColumns.BankAccountId,
    headerName: columnNames.get(SupportedColumns.BankAccountId),
    flex: 0,
    width: 130,
    cellClass: cellWithEllipsis,
    tooltipField: SupportedColumns.BankAccountId,
  },
  {
    field: SupportedColumns.AccountOwner,
    headerName: columnNames.get(SupportedColumns.AccountOwner),
    minWidth: 180,
    cellClass: cellWithEllipsis,
    tooltipField: SupportedColumns.AccountOwner,
  },
  {
    field: SupportedColumns.Bank,
    headerName: columnNames.get(SupportedColumns.Bank),
    minWidth: 150,
    cellClass: cellWithEllipsis,
    tooltipField: SupportedColumns.Bank,
  },
  {
    field: SupportedColumns.AccountNumber,
    headerName: columnNames.get(SupportedColumns.AccountNumber),
    flex: 0,
    width: 130,
    cellClass: cellWithEllipsis,
    tooltipField: SupportedColumns.AccountNumber,
  },
  {
    field: SupportedColumns.InvestmentEntities,
    headerName: columnNames.get(SupportedColumns.InvestmentEntities),
    minWidth: 200,
    cellRenderer: InvestmentEntitiesCellRenderer,
    cellStyle: verticalCenterStyle,
  },
  {
    field: SupportedColumns.Fides,
    headerName: columnNames.get(SupportedColumns.Fides),
    flex: 0,
    width: 100,
    cellRenderer: ProviderStatusCellRenderer,
    cellStyle: centerStyle,
  },
  {
    field: SupportedColumns.RPA,
    headerName: columnNames.get(SupportedColumns.RPA),
    flex: 0,
    width: 100,
    cellRenderer: ProviderStatusCellRenderer,
    cellStyle: centerStyle,
  },
  {
    field: SupportedColumns.FundRecs,
    headerName: columnNames.get(SupportedColumns.FundRecs),
    flex: 0,
    width: 100,
    cellRenderer: ProviderStatusCellRenderer,
    cellStyle: centerStyle,
  },
];
